import Personnummer from 'personnummer';
import { AutocompleteAddressOption } from '@gjensidige/se-common-calculation';
import { validateSSN } from '@gjensidige/se-helpers';

import {
  CommonValidationParams,
  FieldType,
  FieldValidation,
  ValidationResult,
  ValidationRule
} from './types';

export const ageResolver = (ssn: string) => Personnummer.parse(ssn).getAge();

const MIN_YEAR_PREFIX = 19;

const PATTERNS: Record<
  FieldType | 'ssnAge',
  RegExp | ((value: string, params?: CommonValidationParams) => boolean)
> = {
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  zipCode: /^\d{5}$/,
  ssn: (value, params) => {
    if (
      (value.length !== 10 && value.length !== 12) ||
      (value.length === 12 && Number(value.slice(0, 2)) < MIN_YEAR_PREFIX)
    ) {
      return false;
    }

    const allowCoordinationNumber = params?.allowCoordinationNumber;

    if (allowCoordinationNumber) {
      try {
        return Personnummer.valid(value, {
          allowCoordinationNumber: true,
          allowInterimNumber: false
        });
      } catch (e) {
        return false;
      }
    } else {
      return validateSSN(value);
    }
  },
  regNr: (value: string) => {
    return /([A-Z]{3})([0-9]{2})([0-9A-Z])/.test(value) && value.length === 6;
  },
  address: (value: string) => {
    return value.length >= 3 && value.length <= 40;
  },
  autocompleteAddress: (value, params) => {
    if (value && params?.availableOptions?.length) {
      const optionExists = params.availableOptions.some(
        (opt: AutocompleteAddressOption) => opt.objektidentitet === value
      );
      return optionExists;
    } else return false;
  },
  propertyKey: (value) => {
    if (value.length > 50 && value.length <= 3) {
      return false;
    }
    const propertyNameNoWhitespace = value.replace(/\s/g, '');
    const regex = /^[a-zA-ZåäöÅÄÖ.,-]+\d+([:,.]?[a-zA-ZåäöÅÄÖ.,-:\d]+)?$/;
    return regex.test(propertyNameNoWhitespace);
  },
  municipality: (value) => {
    return value.length >= 3 && value.length <= 40;
  },
  phone: (value) => {
    if (!/^[0-9]*$/.test(value) || value.length !== 10) {
      return false;
    }
    return true;
  },
  ssnAge: (value, params) => {
    if (params && params.minAge !== undefined) {
      const allowCoordinationNumber = params?.allowCoordinationNumber;
      let age;

      if (allowCoordinationNumber) {
        try {
          age = Personnummer.parse(value, {
            allowCoordinationNumber: true,
            allowInterimNumber: false
          }).getAge();
        } catch (e) {
          return false;
        }
      } else {
        age = ageResolver(value);
      }
      return age >= params.minAge;
    }
    return true;
  }
};

const ERROR_MESSAGES: Record<
  ValidationRule,
  (fieldName: string, params?: CommonValidationParams) => string
> = {
  required: (fieldName: string) => `${fieldName} måste fyllas i`,
  email: () => 'E-postadressen har fel format.',
  phone: () =>
    'Numret har fel format, ange 10 siffror utan bindestreck eller mellanslag.',
  ssn: () =>
    'Personnumret har fel format, ange 12 siffror i formatet ÅÅÅÅMMDDNNNN.',
  zipCode: () => 'Postnumret har fel format.',
  regNr: () => 'Registreringsnumret har fel format.',
  address: () => 'Adressen har fel format.',
  autocompleteAddress: (fieldName, params) => {
    if (
      params?.addressSearchQuery !== '' &&
      params?.availableOptions?.length === 0
    ) {
      return 'Inga adresser matchar din sökning.';
    }
    return 'Du måste välja en adress från listan.';
  },
  propertyKey: () => 'Fastighetsbeteckningen har fel format.',
  municipality: () => 'Kommunnamnet har fel format.',
  ssnAge: (fieldName, params) => {
    const minAge = params?.minAge;
    return `Du måste vara över ${minAge} år för att köpa en försäkring`;
  }
} as const;

export const validateField = (
  value: string,
  rules: FieldValidation['rules'],
  fieldName: FieldValidation['fieldName'],
  customMessage?: FieldValidation['customMessage'],
  validationParams?: FieldValidation['validationParams']
): ValidationResult => {
  if (rules.includes('required') && (!value || value.trim() === '')) {
    return {
      isValid: false,
      errorMessage: customMessage || ERROR_MESSAGES.required(fieldName)
    };
  }

  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  for (const rule of rules) {
    if (rule === 'required') continue;

    if (rule in PATTERNS) {
      const pattern = PATTERNS[rule as keyof typeof PATTERNS];
      let isValid = false;

      if (typeof pattern === 'function') {
        isValid = pattern(value, validationParams);
      } else {
        isValid = pattern.test(value);
      }

      if (!isValid) {
        const errorMessageKey = rule as keyof typeof ERROR_MESSAGES;

        return {
          isValid: false,
          errorMessage:
            customMessage ||
            (ERROR_MESSAGES[errorMessageKey]
              ? ERROR_MESSAGES[errorMessageKey](fieldName, validationParams)
              : `Invalid ${fieldName}`)
        };
      }
    }
  }

  return { isValid: true };
};

export const validateSwedishMobileNumber = (number: string): boolean => {
  const swedishPhoneRegex = /^(0?7[0236-9])\d{7}$/;
  return swedishPhoneRegex.test(number);
};
